import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { BatchService } from '../../services/batch.service';
import { WorkflowQueryService } from '../../services/workflow-query.service';
import { WorkflowUserService } from '../../services/workflow-user.service';
import { WorkflowUtilitiesService } from '../../services/workflow-utilities.service';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';


/**
 * Component for creating a batch for workflow processing.
 */
@Component({
	selector: 'workflow-batch-add',
	templateUrl: './batch-add.component.html',
	styleUrl: './batch-add.component.css'
})
export class BatchAddComponent implements OnInit {
	@Input() productGroup: any;

	LOCALIZATION: any;

	ready: boolean = true;
	inprogress: boolean = false;
	isNEREnabled: boolean = false;
	isOCRSearchEnabled: boolean = false;
	isSelectedRecordsInvalidFormat: boolean = false;

	pluralMapping = {
		'=0': 'no documents',
		'=1': '1 document ',
		'other': '# documents '
	};

	workflows: any[] = [];
	priorities: any[] = [];
	selectedRecords: any[] = [];
	minDueDate: string = '';

	input = {
		selectedWorkflow: null
	};

	batch = {
		name: '',
		priority: 'Medium',
		workflow: { config: {}, parameters: [] } as { config: any; parameters: any[] },
		comments: null,
		dueDate: null as Date | null
	};

	constructor(
		private alertsService: AlertsService,
		private userService: UserService,
		private workflowUserService: WorkflowUserService,
		private dxUtilsService: WorkflowUtilitiesService,
		private workflowQueryService: WorkflowQueryService,
		private batchService: BatchService,
		private ngbmodalService: NgbActiveModal
	) { 
		dayjs.extend(customParseFormat);
	}

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.workflowQueryService.isNEREnabled().then(
			(response: any) => {
				this.isNEREnabled = true;
			},
			(err: any) => {
				this.isNEREnabled = false;
			}
		);

		this.workflowQueryService.isOCRTunedSearchEnabled().then(
			(response: any) => {
				this.isOCRSearchEnabled = true;
			},
			(err: any) => {
				this.isOCRSearchEnabled = false;
			}
		);

		this.checkIsReady();
		this.processSelectedRecords();
		this.clearSelections();
		this.loadWorkflows();
		this.loadPriorities();
		this.minDueDate = this.getTomorrowAsFormattedStr();
	}

	loadPriorities(): void {
		this.batchService.getPriorities().then(
			(response: any) => {
				this.priorities = response;
			},
			(err: any) => {
				console.error("Failed to load priorities", err);
				this.priorities = [];
			}
		);
	}

	loadWorkflows(): void {
		this.batchService.getWorkflows().then(
			(response: any) => {
				this.workflows = response;

				// Load the default workflow, if there is one
				this.workflows.forEach((w: any) => {
					if (w.default) {
						this.input.selectedWorkflow = w;
						this.loadSelectedWorkflow();
					}
				});

				// No default - If there is only one option, load it
				if (this.workflows.length == 1) {
					this.input.selectedWorkflow = this.workflows[0];
					this.loadSelectedWorkflow();
				}
			},
			(err: any) => {
				console.error("Failed to load workflows", err);
				this.alertsService.error({
					message: "Failed to retrieve available workflows."
				});
				this.workflows = [];
			}
		);
	}

	loadSelectedWorkflow(): void {
		if (this.input.selectedWorkflow) {
			// Load the form based on the selected workflow params
			this.batch.workflow = this.input.selectedWorkflow;
			this.loadParticipants();

			// Set the ocr tuned search parameter to FALSE by default,
			// as we do not normally use ocr confidence
			if (this.batch.workflow) {
				(this.batch.workflow.parameters || []).forEach((parameter: any) => {
					if ('QUERY' == parameter.type) {
						parameter.values.ocr_tuned_search = false;
					}
				});
			}
			console.log("Loaded workflow:");
			console.log(this.batch.workflow);
		} else {
			this.batch.workflow = { config: {}, parameters: [] };
		}
	}

	private loadParticipants(): void {
		if (this.batch.workflow) {
			(this.batch.workflow.parameters || []).forEach((parameter: any) => {
				if ('PARTICIPANT' == parameter.type) {
					this.workflowUserService.getParticipantsByGroup(parameter.values.jbpm_usertask_group, true).then(
						(response: any) => {
							parameter.values.participants = response;

							// Collate into a set of unique group names
							parameter.values.group_participants = response
								.map((obj: any) => obj.loginGroupName)
								.filter((name: string, index: number, array: any) => array.indexOf(name) === index);

							console.log(parameter.values.group_participants);
						},
						function(err) {
							console.error("Failed to load participants", err);
						}
					);
				}
			});
		}
	}

	private clearSelections(): void {
		this.workflowQueryService.clearSelectedQueries();
		this.workflowQueryService.clearSelectedQueryLists();
		this.workflowQueryService.clearSelectedNER();
	}

	private checkIsReady(): void {
		if (!this.dxUtilsService.enabled) {
			console.log("BP Engine not enabled");
			this.ready = false;
			return;
		}

		this.dxUtilsService.getEngineReadyState().then(
			(response: any) => {
				this.ready = true;
			},
			(err: any) => {
				console.error("Failed to get engine ready state", err);
				this.ready = false;
			}
		);
	}

	private processSelectedRecords(): void {
		this.productGroup.records.forEach((r: any) => {
			if (this.productGroup.ids.indexOf(r.id) !== -1) {
				console.log("Record id:%s, format:%s", r.id, r.format);

				// Tag unsupported record types
				r.dxvalid = false;
				if (!r.format) {
					// Optimistic - format will be validated server-side
					r.dxvalid = true;
				} else {
					if (Array.isArray(r.format)) {
						for (let i = 0; i < r.format.length; i++) {
							if ("PDF" == r.format[i].toUpperCase()) {
								r.dxvalid = true;
							} else {
								r.dxvalid = false;
								this.isSelectedRecordsInvalidFormat = true;
								r.uidescription = "Unsupported format: " + r.format[i];
							}
						}
					}
					else if ("PDF" == r.format.toUpperCase()) {
						r.dxvalid = true;
					} else {
						this.isSelectedRecordsInvalidFormat = true;
						r.uidescription = "Unsupported format";
					}
				}
				this.selectedRecords.push(r);
			}
		});
	}

	remove(): void {
		this.selectedRecords = this.selectedRecords.filter((r: any) => {
			return !r.ignore;
		});
		this.isSelectedRecordsInvalidFormat = this.selectedRecords.some(r => !r.dxvalid);
	}

	onSave(): void {
		this.inprogress = true;

		// Validate required parameters
		let failedValidation = [];
		
		// Date format from HTML <input> type="date" implementation
		let batchDueDate = null;
		if (this.batch.dueDate) {
			batchDueDate = dayjs(this.batch.dueDate, "YYYY-MM-DD").valueOf();
		}
		
		let payload = {
			workflowId: this.batch.workflow.config.name,
			batchName: this.batch.name,
			dueDate: batchDueDate,
			priority: this.batch.priority,
			products: [] as number[],
			comment: {} as any,
			parameters: {} as any
		};

		this.selectedRecords.forEach((r: any) => {
			if (!r.ignore) payload.products.push(r.id);
		});

		if (payload.products.length == 0) { failedValidation.push('No documents selected'); }
		if (this.selectedRecords.some(r => !r.dxvalid)) { failedValidation.push('Invalid document formats selected'); }
		if (this.dxUtilsService.isEmptyString(this.batch.name)) { failedValidation.push('Batch Name'); }
		
		if (this.batch.comments) {
			payload.comment = {
				message: this.batch.comments,
				commentorId: this.userService.user.username,
				commentDate: new Date().getTime()
			};
		}

		payload.parameters = this.batch.workflow.parameters;
		this.batch.workflow.parameters.forEach((p: any) => {
			if ("QUERY" == p.type) {
				let selectedQueries = this.workflowQueryService.getSelectedQueries();
				if (selectedQueries.length > 0) {
					p.values.query_ids = selectedQueries.toString();
				}
				let selectedQueryLists = this.workflowQueryService.getSelectedQueryLists();
				if (selectedQueryLists.length > 0) {
					p.values.query_list_ids = selectedQueryLists.toString();
				}
				let selectedNER = this.workflowQueryService.getSelectedNER();
				if (selectedNER.length > 0) {
					p.values.ner_ids = selectedNER.toString();
				}
				if (p.required) {
					if (this.dxUtilsService.isEmptyString(p.values.query_ids) &&
						this.dxUtilsService.isEmptyString(p.values.query_list_ids) &&
						this.dxUtilsService.isEmptyString(p.values.ner_ids)) {
						failedValidation.push('Queries');
					}
				}
			}
			else if ("PARTICIPANT" == p.type) {
				// If parameter.values.awdx_user_id is not null, assign directly to that user.
				// If parameter.values.awdx_group_id is not null, assign to any user in that group (with the appropriate BP role).
				// Otherwise, assign to any user with a BP role in parameter.values.jbpm_usertask_group.
				// Defaults to supervisor assignment
				if (p.values.is_jbpm_usertask_group_assignment) {
					// We're assigning to the BP role -- server will use p.values.jbpm_usertask_group
					p.values.awdx_user_id = null;
					p.values.awdx_group_id = null;
				} else if (p.values.awdx_group_id && p.values.awdx_group_id.length > 0) {
					p.values.awdx_user_id = null;
					p.values.is_jbpm_usertask_group_assignment = null;
				} else {
					// We're assigning directly to a user
				}
				if (p.required) {
					if (this.dxUtilsService.isEmptyString(p.values.awdx_user_id) &&
						this.dxUtilsService.isEmptyString(p.values.awdx_group_id) &&
						!p.values.is_jbpm_usertask_group_assignment) {
						failedValidation.push('Participant');
					}
				}
			}
			else if ("DATE" == p.type) {
				p.values = { 'dateValue': p.value };
				delete p.value;
			}
			else if ("STRING" == p.type) {
				p.values = { 'stringValue': p.value };
				delete p.value;
			}
			else if ("BOOLEAN" == p.type) {
				p.values = { 'booleanValue': p.value };
				delete p.value;
			}
			else if ("LIST" == p.type) {
				p.values = { 'listValue': p.value };
				delete p.value;
			}
		});

		if (failedValidation.length > 0) {
			let msg = this.LOCALIZATION.fillAllFields + "<br>" + failedValidation.join('<br>');
			this.handleBatchError({ errtitle: "Form incomplete", errmsg: msg });
			return;
		}

		// Issue a test query to determine if the batch name is unique before submitting the form
		this.batchService.uniqueBatchNameTest(this.batch.name).then(
			(response: any) => {
				// Passed
				this.save(payload);
			},
			(err: any) => {
				this.handleBatchError({ errtitle: "Batch validation error", errmsg: "The batch name is not unique." });
			}
		);
	}

	private save(payload: any): void {
		payload.parameters.forEach((p: any) => {
			if ("PARTICIPANT" == p.type) {
				// Remove the set of potential participants (for UI usage, only)
				delete p.values.participants;
				delete p.values.group_participants;
			}
		});

		this.batchService.createBatch(payload).then(
			(response: any) => {
				this.alertsService.success({
					message: "Created new batch: " + payload.batchName,
					delay: 5000
				});
				this.startWorkflow(response);
				this.close();
			},
			(err: any) => {
				err = ((err && err.statusText) || "Batch creation failed. Please see the log for details.");
				console.error("Failed to create batch", err);
				this.handleBatchError({ errtitle: "Batch error", errmsg: err });
			}
		);
	}

	private startWorkflow(batchId: number) {
		this.batchService.startWorkflow(batchId).then(
			(response: any) => {
				this.inprogress = false;
			},
			(err: any) => {
				console.error("Failed to start workflow", err);
				this.handleBatchError({ errtitle: "Batch error", errmsg: "Failed to start the batch workflow." });
			}
		);
	}

	private handleBatchError(args: { errtitle: string, errmsg: string }): void {
		setTimeout(() => {
			this.inprogress = false;
		}, 1000);

		this.alertsService.error({
			title: args.errtitle,
			message: args.errmsg
		});
	}

	private getTomorrowAsFormattedStr(): string {
		let d = new Date();
		d.setDate(d.getDate() + 1);
		let date = [
			d.getFullYear(),
			('0' + (d.getMonth() + 1)).slice(-2),
			('0' + d.getDate()).slice(-2)
		].join('-');
		return date;
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
